/**
 * THE product-analytics event taxonomy. Single source of truth, shared by web,
 * mobile and the server: every emit path (`track()` on web/mobile,
 * `captureEvent()` / `captureProduct()` on the server) is typed to
 * `ObservabilityEvent`, so an event that is not declared here fails to compile.
 * Add the event here first, then emit it.
 *
 * Conventions (enforced by events.test.ts):
 * - Name: `<domain>.<verb>`, snake_case on both sides, verb in the past tense
 *   for facts (`device.claimed`, not `device.claim`).
 * - Properties carry ids and enum-ish facts ONLY — never emails, names, titles,
 *   or message content. PostHog is a third-party sink; names belong in
 *   `audit_logs`.
 * - `emitter: "server"` means the event is emitted from the api-server and is
 *   therefore trustworthy (an ad-blocker, a declined consent banner or a dead
 *   network cannot lose it). Anything a business decision depends on — sign-up,
 *   pairing, playback, trial and revenue state — MUST be server-emitted.
 * - Kiosk/player surfaces emit nothing at all: see `kiosk-policy.ts`.
 *
 * The funnel this taxonomy has to answer, end to end:
 *   visit → sign-up → onboarding complete → device paired → first playback →
 *   trial → paid conversion → churn, plus activation (a workspace with at least
 *   one device actually playing) and expansion (devices added over time).
 */

export type EventEmitter = "server" | "client";

export type FunnelStage =
  /** Getting an account into existence: visit → sign-up → onboarded. */
  | "acquisition"
  /** Getting to value: a paired device with audio actually coming out of it. */
  | "activation"
  /** Feature adoption once activated. */
  | "engagement"
  /** Trial and first payment. */
  | "revenue"
  /** Growth inside an existing account (more devices). */
  | "expansion"
  /** Leaving: failed payment, cancellation. */
  | "churn";

export interface EventSpec {
  /** Where this event is emitted from, and therefore how far it can be trusted. */
  emitter: EventEmitter;
  /** Which funnel question it answers. */
  stage: FunnelStage;
  /** One-line meaning — this doubles as the docs table. */
  doc: string;
  /**
   * Emitted at most once per subject per UTC day (server-side dedupe). Marks
   * events that would otherwise fire on every request or every track change.
   */
  daily?: true;
  /**
   * Declared for a player/kiosk surface and therefore SUPPRESSED at runtime by
   * the kiosk analytics policy — it can never reach PostHog. Kept only so the
   * pre-existing kiosk call site still type-checks; that call site should be
   * deleted. Do not add new ones.
   */
  kioskSuppressed?: true;
}

export const EVENT_REGISTRY = {
  // -- Acquisition ---------------------------------------------------------
  "auth.signed_up": {
    emitter: "server",
    stage: "acquisition",
    doc: "A Clerk identity was provisioned into a local user row for the first time. The head of the funnel.",
  },
  "auth.signed_in": {
    emitter: "server",
    stage: "acquisition",
    doc: "An authenticated request arrived for a user — the server-side daily-active signal (survives ad-blockers).",
    daily: true,
  },
  "auth.signed_out": {
    emitter: "client",
    stage: "acquisition",
    doc: "The web session ended (explicit sign-out).",
  },
  "workspace.created": {
    emitter: "server",
    stage: "acquisition",
    doc: "A workspace row was created for an owner.",
  },
  "workspace.onboarded": {
    emitter: "server",
    stage: "acquisition",
    doc: "The onboarding wizard completed (or was skipped — see the `skipped` property).",
  },
  "workspace.selected": {
    emitter: "client",
    stage: "engagement",
    doc: "The operator switched the active workspace.",
  },
  "member.invited": {
    emitter: "server",
    stage: "engagement",
    doc: "A team invite was sent.",
  },

  // -- Activation ----------------------------------------------------------
  "device.claimed": {
    emitter: "server",
    stage: "activation",
    doc: "A player was paired to a workspace.",
  },
  "device.activated": {
    emitter: "server",
    stage: "activation",
    doc: "A device was flipped active — the billable moment, it adds a license.",
  },
  "device.playback_started": {
    emitter: "server",
    stage: "activation",
    doc: "A device reported a play we durably recorded — proof that audio/video actually ran at the venue. Emitted server-side from the realtime store, so web, desktop, TV and mobile players are covered identically with no client work.",
    daily: true,
  },

  // -- Engagement ----------------------------------------------------------
  "player.started": {
    emitter: "client",
    stage: "engagement",
    doc: "Legacy web-player open. SUPPRESSED by the kiosk analytics policy — `device.playback_started` replaces it.",
    kioskSuppressed: true,
  },
  "player.error": {
    emitter: "client",
    stage: "engagement",
    doc: "Legacy web-player error. SUPPRESSED by the kiosk analytics policy — player faults belong in `device_events` and Sentry.",
    kioskSuppressed: true,
  },
  "remote.command_sent": {
    emitter: "client",
    stage: "engagement",
    doc: "An operator sent a realtime transport command to a device.",
  },
  "quickplay.used": {
    emitter: "server",
    stage: "engagement",
    doc: "A persisted quick play was started.",
  },
  "broadcast.started": {
    emitter: "server",
    stage: "engagement",
    doc: "A live broadcast/announcement session was opened.",
  },
  "playlist.created": {
    emitter: "server",
    stage: "engagement",
    doc: "A playlist was created.",
  },
  "schedule.created": {
    emitter: "server",
    stage: "engagement",
    doc: "A schedule was created.",
  },
  "station.created": {
    emitter: "server",
    stage: "engagement",
    doc: "A smart playlist (station) was created.",
  },
  "station.ai_drafted": {
    emitter: "server",
    stage: "engagement",
    doc: "Station rules were drafted by the AI helper.",
  },
  "media.uploaded": {
    emitter: "server",
    stage: "engagement",
    doc: "A workspace upload reached `ready`.",
  },
  "media.tts_generated": {
    emitter: "server",
    stage: "engagement",
    doc: "A TTS announcement was rendered to an asset.",
  },
  "media.tts_draft_generated": {
    emitter: "server",
    stage: "engagement",
    doc: "A TTS draft was previewed.",
  },
  "report.exported": {
    emitter: "server",
    stage: "engagement",
    doc: "A CSV export ran (plays / uptime / play-logs).",
  },
  "support.ticket_created": {
    emitter: "client",
    stage: "engagement",
    doc: "A support request was submitted.",
  },

  // -- Revenue / expansion / churn ----------------------------------------
  "trial.started": {
    emitter: "server",
    stage: "revenue",
    doc: "A subscription row was created in `trialing` — the 30-day, no-card trial began.",
  },
  "subscription.converted": {
    emitter: "server",
    stage: "revenue",
    doc: "The account reached `active` from a non-paying state — the trial→paid conversion.",
  },
  "subscription.licenses_changed": {
    emitter: "server",
    stage: "expansion",
    doc: "The billed device-license quantity moved (positive `delta` = expansion, negative = contraction).",
  },
  "subscription.payment_failed": {
    emitter: "server",
    stage: "churn",
    doc: "The account entered `past_due` / `unpaid` — a charge failed and Stripe is retrying.",
  },
  "subscription.canceled": {
    emitter: "server",
    stage: "churn",
    doc: "The subscription reached `canceled` — churn.",
  },
} as const satisfies Record<string, EventSpec>;

export type ObservabilityEvent = keyof typeof EVENT_REGISTRY;

/**
 * `{ "media.uploaded": "media.uploaded", … }` — the ergonomic form call sites
 * use so a typo is a compile error rather than a silently orphaned event.
 */
export const OBSERVABILITY_EVENTS = Object.fromEntries(
  Object.keys(EVENT_REGISTRY).map((name) => [name, name]),
) as { [K in ObservabilityEvent]: K };

export const OBSERVABILITY_EVENT_NAMES = Object.keys(
  EVENT_REGISTRY,
) as ObservabilityEvent[];

/** Widened lookup — `EVENT_REGISTRY[name]` alone is a union of literal shapes. */
export function eventSpec(name: ObservabilityEvent): EventSpec {
  return EVENT_REGISTRY[name];
}

export function eventsForStage(stage: FunnelStage): ObservabilityEvent[] {
  return OBSERVABILITY_EVENT_NAMES.filter(
    (name) => eventSpec(name).stage === stage,
  );
}
